import React, { useEffect, useCallback, useState } from "react";
import MainLayout from "../../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../assets/styles/custom-styles.css";
import useNotification from "../../../../hooks/useNotification";
import useLoader from "../../../../hooks/useLoader";
import useForm from "../../../../hooks/useForm";
import Form from "../../../../components/forms/Form";
import { shipmentItemFields } from "../../../../constants/forms/shipmentItemFields";
import {
  normalizeFormErrors,
  setDefaultFieldValues,
  transformObjectValues,
} from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import { entities } from "../../../../constants/entities";
import PageHeader from "../../../../components/PageHeader";
import SectionHeader from "../../../../components/forms/SectionHeader";
import SimpleBody from "../../../../components/forms/SimpleBody";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const EditShipmentItemPage = () => {
  const { movementId, shipmentId, itemId } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();
  const { formData, handleChange, setFormData, formatData } = useForm(
    setDefaultFieldValues(shipmentItemFields),
  );
  const queryClient = useQueryClient();

  const {
    put: baseServicePut,
    getByColumn: baseServiceGetByColumn,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate);

  const [customerId, setCustomerId] = useState(null);

  // --- Query para buscar detalhes do Item de Carregamento ---
  const {
    data: shipmentItemDetails,
    isLoading: isLoadingShipmentItem,
    isError: isErrorShipmentItem,
    error: errorShipmentItem,
  } = useQueryWithLoader({
    queryKey: ["shipmentItem", movementId, shipmentId, itemId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.movements.movementShipmentItem(movementId, shipmentId, itemId),
      );
      if (!response.result) {
        throw new Error(
          response.message ||
            "Falha ao carregar detalhes do item de carregamento.",
        );
      }
      return response.result;
    },
    enabled: !!movementId && !!shipmentId && !!itemId,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // --- useEffect para tratar erros na query de detalhes do Item de Carregamento ---
  useEffect(() => {
    if (isErrorShipmentItem && errorShipmentItem) {
      const errorMessage =
        errorShipmentItem.response?.data?.error ||
        "Erro ao carregar os dados do item de carregamento.";
      showNotification("error", errorMessage);
      console.error(
        "Erro no carregamento dos dados do item de carregamento:",
        errorShipmentItem,
      );
    }
  }, [isErrorShipmentItem, errorShipmentItem, showNotification]);

  // --- Query para buscar detalhes do Movimento (para obter customer_id) ---
  const {
    data: movementDetails,
    isLoading: isLoadingMovement,
    isError: isErrorMovement,
    error: errorMovement,
  } = useQueryWithLoader({
    queryKey: ["movementDetails", movementId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.movements.getById(movementId),
      );
      if (!response.result) {
        throw new Error(
          response.message || "Falha ao carregar detalhes do movimento.",
        );
      }
      return response.result;
    },
    enabled: !!movementId, // Habilita a query apenas quando movementId está disponível
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // --- useEffect para tratar erros na query de detalhes do Movimento ---
  useEffect(() => {
    if (isErrorMovement && errorMovement) {
      const errorMessage =
        errorMovement.response?.data?.error ||
        "Erro ao carregar os dados do movimento.";
      showNotification("error", errorMessage);
      console.error(
        "Erro no carregamento dos dados do movimento:",
        errorMovement,
      );
    }
  }, [isErrorMovement, errorMovement, showNotification]);

  // --- useEffect para atualizar customerId e formatação de dados do item de carregamento ---
  useEffect(() => {
    if (shipmentItemDetails && movementDetails) {
      setCustomerId(movementDetails.customer_id);
      formatData(shipmentItemDetails, shipmentItemFields);
      // Formata movement_item_id se for um select (espera { value, label })
      setFormData((prev) => ({
        ...prev,
        movement_item_id: shipmentItemDetails.movement_item_id,
      }));
    }
  }, [shipmentItemDetails, movementDetails, formatData, setFormData]);

  // --- Query para buscar Endereços do Cliente ---
  const {
    data: addresses = [],
    isLoading: isLoadingAddresses,
    isError: isErrorAddresses,
    error: errorAddresses,
  } = useQueryWithLoader({
    queryKey: ["customerAddresses", customerId],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.customers.addresses.get(customerId),
      );
      if (!response.result) {
        throw new Error(response.message || "Falha ao carregar endereços.");
      }
      return response.result.data.map((address) => ({
        value: address.id,
        label: `${address.street}, ${address.city} - ${address.state}`,
      }));
    },
    enabled: !!customerId, // Habilita a query apenas quando customerId está disponível
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // --- useEffect para tratar erros na query de endereços ---
  useEffect(() => {
    if (isErrorAddresses && errorAddresses) {
      const errorMessage =
        errorAddresses.response?.data?.error ||
        "Erro ao carregar os endereços.";
      showNotification("error", errorMessage);
      console.error("Erro no carregamento dos endereços:", errorAddresses);
    }
  }, [isErrorAddresses, errorAddresses, showNotification]);

  // --- Query para buscar Localizações do Endereço Selecionado ---
  const {
    data: locations = [],
    isLoading: isLoadingLocations,
    isError: isErrorLocations,
    error: errorLocations,
  } = useQueryWithLoader({
    queryKey: [
      "addressLocations",
      customerId,
      formData.address_id?.value || formData.address_id,
    ],
    queryFn: async () => {
      // Pega o valor do address_id, lidando com o formato {value, label} ou direto
      const addressIdValue = formData.address_id?.value || formData.address_id;
      const response = await baseServiceGet(
        entities.customers.customerAddressLocations(customerId, addressIdValue),
        { deleted_at: false },
      );
      if (!response.result) {
        throw new Error(response.message || "Falha ao carregar localizações.");
      }
      return response.result.data.map((location) => {
        const parts = [location.area];

        if (location.section) {
          parts.push(location.section);
        }

        if (location.spot) {
          parts.push(`- ${location.spot}`);
        }

        return {
          value: location.id,
          label: parts.join(", "),
        };
      });
    },
    enabled:
      !!customerId && !!(formData.address_id?.value || formData.address_id), // Habilita a query apenas quando customerId e address_id estão disponíveis
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  // --- useEffect para tratar erros na query de localizações ---
  useEffect(() => {
    if (isErrorLocations && errorLocations) {
      const errorMessage =
        errorLocations.response?.data?.error ||
        "Erro ao carregar as localizações.";
      showNotification("error", errorMessage);
      console.error("Erro no carregamento das localizações:", errorLocations);
    }
  }, [isErrorLocations, errorLocations, showNotification]);

  // --- Gerenciamento do Loader Global ---
  useEffect(() => {
    if (
      isLoadingShipmentItem ||
      isLoadingMovement ||
      isLoadingAddresses ||
      isLoadingLocations
    ) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [
    isLoadingShipmentItem,
    isLoadingMovement,
    isLoadingAddresses,
    isLoadingLocations,
    showLoader,
    hideLoader,
  ]);

  const getOptions = useCallback(
    (fieldId) => {
      switch (fieldId) {
        case "address_id":
          return addresses;
        case "location_id":
          return locations;
        // Adicione outros cases para outros campos de seleção conforme necessário
        default:
          return [];
      }
    },
    [addresses, locations],
  );

  const getSelectedValue = useCallback(
    (fieldId) => {
      const value = formData[fieldId];
      // Lida com casos onde o valor já pode ser um objeto {value, label} ou apenas o valor
      const actualValue =
        typeof value === "object" && value !== null && "value" in value
          ? value.value
          : value;
      return (
        getOptions(fieldId).find((option) => option.value === actualValue) ||
        null
      );
    },
    [formData, getOptions],
  );

  // --- Mutation para atualizar o Item de Carregamento ---
  const updateShipmentItemMutation = useMutation({
    mutationFn: async (dataToUpdate) => {
      // Usando a entidade específica para atualizar o item de carregamento
      const response = await baseServicePut(
        entities.movements.movementShipmentItemUpdate(
          movementId,
          shipmentId,
          itemId,
        ),
        dataToUpdate,
      );
      if (!response.result) {
        throw new Error(
          response.message || "Falha ao atualizar item de carregamento.",
        );
      }
      return response;
    },
    onSuccess: () => {
      // Invalida as queries relevantes para rebuscar os dados atualizados
      queryClient.invalidateQueries({
        queryKey: ["shipmentItem", movementId, shipmentId, itemId],
      });
      queryClient.invalidateQueries({
        queryKey: ["shipmentItems", movementId, shipmentId],
      }); // Para a lista na página de detalhes do carregamento
      navigate(
        `/aluguel/movimentos/${movementId}/carregamentos/${shipmentId}/detalhes/`,
      );
    },
    onError: (error) => {
      console.error("onError ", error);
      // Se o erro contém 'formErrors', podemos setar no estado
      if (error.response?.data?.formErrors) {
        setFormErrors(error.response.data.formErrors);
      }
      hideLoader();
    },
    onSettled: () => {
      hideLoader(); // Esconde o loader após a mutação (sucesso ou erro)
    },
  });

  const handleSubmit = async () => {
    try {
      showLoader();
      const transformedData = transformObjectValues(formData);

      const cleanedData = Object.fromEntries(
        Object.entries(transformedData).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );

      // Chama a mutação para enviar os dados
      await updateShipmentItemMutation.mutateAsync(cleanedData);
    } catch (error) {
      console.error("Erro ao enviar item de carregamento:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = () => {
    navigate(
      `/aluguel/movimentos/${movementId}/carregamentos/${shipmentId}/detalhes/`,
    );
  };

  const handleFieldChange = useCallback(
    (fieldId, value) => {
      // Removido 'field' pois não está sendo usado
      handleChange(fieldId, value);
    },
    [handleChange],
  ); // Dependência corrigida para handleChange

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Editar Item do Carregamento"
        showBackButton={true}
        backUrl={`/aluguel/movimentos/${movementId}/carregamentos/${shipmentId}/detalhes/`}
      />
      <div className="container-fluid p-1">
        {isLoadingShipmentItem || isLoadingMovement ? (
          <div>Carregando dados...</div> // Exibe um indicador de carregamento inicial
        ) : (
          <Form
            initialFormData={formData}
            onSubmit={handleSubmit}
            textSubmit="Salvar Edição"
            textLoadingSubmit="Salvando..."
            handleBack={handleBack}
            isSubmitting={updateShipmentItemMutation.isPending} // Passa o estado de submissão da mutation
          >
            {() =>
              shipmentItemFields.map((section) => (
                <React.Fragment key={section.id || section.name}>
                  {" "}
                  {/* Adicionando key para o fragmento */}
                  <SectionHeader
                    section={section.section}
                    viewTable={false}
                    setViewTable={() => {}}
                    addFieldsInData={() => {}}
                  />
                  <SimpleBody
                    fields={section.fields}
                    formErrors={normalizeFormErrors(formErrors)}
                    formData={formData}
                    handleFieldChange={handleFieldChange} // Usando handleFieldChange aqui
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                  />
                </React.Fragment>
              ))
            }
          </Form>
        )}
      </div>
    </MainLayout>
  );
};

export default EditShipmentItemPage;
